
import { getAdminToken } from './auth';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


const adminHeaders = (extra = {}) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getAdminToken()}`,
  ...extra,
});

const handle = async (res) => {
  const data = await res.json();
  if (!data.success) {
    if (res.status === 401) {
      localStorage.removeItem('aamira_admin');
      localStorage.removeItem('aamira_admin_token');
      window.location.href = '/admin/login';
      return;
    }
    throw new Error(data.error || 'Request failed');
  }
  return data;
};


// ════════════════════════════════════════════════════════════
//  IMAGE UPLOAD
// ════════════════════════════════════════════════════════════

/**
 * Upload a single file to Supabase Storage via backend
 * @param {File}   file   — browser File object
 * @param {'product-images'|'reel-covers'} bucket
 * @returns {Promise<string>}  public URL
 */
export const uploadImage = async (file, bucket = 'product-images') => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API}/admin/upload?bucket=${bucket}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAdminToken()}` }, // no Content-Type — browser sets multipart boundary
    body: formData,
  });

  const data = await res.json();
  if (!data.success) {
    if (res.status === 401) {
      localStorage.removeItem('aamira_admin');
      localStorage.removeItem('aamira_admin_token');
      window.location.href = '/admin/login';
      return;
    }
    throw new Error(data.error || 'Upload failed');
  }
  return data.url;
};

// ════════════════════════════════════════════════════════════
//  ORDERS
// ════════════════════════════════════════════════════════════

export const getAdminOrders = async () => {
  const res  = await fetch(`${API}/admin/orders`, { headers: adminHeaders() });
  const data = await handle(res);
  return data.orders;
};

export const updateOrderStatus = async (id, status, tracking_number = '') => {
  const res  = await fetch(`${API}/admin/orders/${id}/status`, {
    method:  'PUT',
    headers: adminHeaders(),
    body:    JSON.stringify({ status, tracking_number }),
  });
  const data = await handle(res);
  return data.order;
};

// ════════════════════════════════════════════════════════════
//  PRODUCTS
// ════════════════════════════════════════════════════════════

/** Get all products (with their collections) */
export const getAdminProducts = async () => {
  const res  = await fetch(`${API}/admin/products`, { headers: adminHeaders() });
  const data = await handle(res);
  return data.products;
};

/**
 * Create a new product
 * @param {object} product  — product fields
 * @param {string[]} collection_ids — array of collection UUIDs
 */
export const createProduct = async (product, collection_ids = []) => {
  const res  = await fetch(`${API}/admin/products`, {
    method:  'POST',
    headers: adminHeaders(),
    body:    JSON.stringify({ ...product, collection_ids }),
  });
  const data = await handle(res);
  return data.product;
};

/**
 * Update an existing product
 * @param {string}   id
 * @param {object}   updates
 * @param {string[]} collection_ids  — pass current selection to re-sync
 */
export const updateProduct = async (id, updates, collection_ids) => {
  const res  = await fetch(`${API}/admin/products/${id}`, {
    method:  'PUT',
    headers: adminHeaders(),
    body:    JSON.stringify({ ...updates, collection_ids }),
  });
  const data = await handle(res);
  return data.product;
};

/** Delete a product (also removes product_collections rows) */
export const deleteProduct = async (id) => {
  const res = await fetch(`${API}/admin/products/${id}`, {
    method:  'DELETE',
    headers: adminHeaders(),
  });
  return handle(res);
};


// ════════════════════════════════════════════════════════════
//  REELS
// ════════════════════════════════════════════════════════════

/** Get all reels */
export const getAdminReels = async () => {
  const res  = await fetch(`${API}/admin/reels`, { headers: adminHeaders() });
  const data = await handle(res);
  return data.reels;
};

/** Create a new reel */
export const createReel = async (reel) => {
  const res  = await fetch(`${API}/admin/reels`, {
    method:  'POST',
    headers: adminHeaders(),
    body:    JSON.stringify(reel),
  });
  const data = await handle(res);
  return data.reel;
};

/** Update a reel */
export const updateReel = async (id, updates) => {
  const res  = await fetch(`${API}/admin/reels/${id}`, {
    method:  'PUT',
    headers: adminHeaders(),
    body:    JSON.stringify(updates),
  });
  const data = await handle(res);
  return data.reel;
};

/** Delete a reel */
export const deleteReel = async (id) => {
  const res = await fetch(`${API}/admin/reels/${id}`, {
    method:  'DELETE',
    headers: adminHeaders(),
  });
  return handle(res);
};


// ════════════════════════════════════════════════════════════
//  COLLECTIONS
// ════════════════════════════════════════════════════════════

/** Get all collections */
export const getAdminCollections = async () => {
  const res  = await fetch(`${API}/admin/collections`, { headers: adminHeaders() });
  const data = await handle(res);
  return data.collections;
};

/** Create a new collection */
export const createCollection = async (collection) => {
  const res  = await fetch(`${API}/admin/collections`, {
    method:  'POST',
    headers: adminHeaders(),
    body:    JSON.stringify(collection),
  });
  const data = await handle(res);
  return data.collection;
};

/** Update a collection */
export const updateCollection = async (id, updates) => {
  const res  = await fetch(`${API}/admin/collections/${id}`, {
    method:  'PUT',
    headers: adminHeaders(),
    body:    JSON.stringify(updates),
  });
  const data = await handle(res);
  return data.collection;
};

/** Delete a collection (also removes product_collections links) */
export const deleteCollection = async (id) => {
  const res = await fetch(`${API}/admin/collections/${id}`, {
    method:  'DELETE',
    headers: adminHeaders(),
  });
  return handle(res);
};


// ════════════════════════════════════════════════════════════
//  BANNERS
// ════════════════════════════════════════════════════════════

export const getAdminBanners = async () => {
  const res = await fetch(`${API}/admin/banners`, { headers: adminHeaders() });
  const data = await handle(res);
  return data.banners;
};

export const createBanner = async (banner) => {
  const res = await fetch(`${API}/admin/banners`, {
    method: 'POST', headers: adminHeaders(), body: JSON.stringify(banner),
  });
  const data = await handle(res);
  return data.banner;
};

export const updateBanner = async (id, updates) => {
  const res = await fetch(`${API}/admin/banners/${id}`, {
    method: 'PUT', headers: adminHeaders(), body: JSON.stringify(updates),
  });
  const data = await handle(res);
  return data.banner;
};

export const deleteBanner = async (id) => {
  const res = await fetch(`${API}/admin/banners/${id}`, {
    method: 'DELETE', headers: adminHeaders(),
  });
  return handle(res);
};